import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, CheckCircle, Plus, MoreVertical, Calendar, Truck, User, Tag, CreditCard, LayoutGrid, X } from 'lucide-react';
import { Dropdown, Checkbox, message } from 'antd';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import BulkStatusUpdateModal from '../components/Bulkstatusupdatemodal';
import { ordersApi, employeesApi, authFetch } from '../services/api';
import { authHelpers } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import ThemeSelect from '../components/ThemeSelect';
import Pagination from '../components/Pagination';
import { BASE_URL as API_URL } from '../services/api';
function DispatchOrder() {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourier, setSelectedCourier] = useState('Couriers partner');
  const [selectedService, setSelectedService] = useState('All courier');
  const [selectedEmployee, setSelectedEmployee] = useState('All employees');
  const [selectedDateFilter, setSelectedDateFilter] = useState('All Time');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    agent: '',
    category: '',
    status: '',
    paymentMode: ''
  });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalOrders, setTotalOrders] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [orderLimit, setOrderLimit] = useState(20);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);

  // Helper function to get current user from localStorage
  const getCurrentUser = () => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      return {};
    }
  };

  const courierOptions = ['Couriers partner', 'DNST', 'Ekart', 'E-kart', 'F-ship', 'Shadowfax', 'Speed post', 'VELOCITY'];
  const uniqueServices = [...new Set(orders.map(o => o.courierName).filter(Boolean))];
  const uniqueAgents = [...new Set(orders.map(o => o.createdByName || o.agentName).filter(Boolean))];
  const uniqueCategories = [...new Set(orders.map(o => o.category).filter(Boolean))];
  const uniqueStatuses = [...new Set(orders.map(o => o.status || o.orderStatus).filter(Boolean))];
  const dateOptions = ['All Time', 'Today', 'Yesterday', 'Last Week', 'Last Month', 'Last Six Months'];

  useEffect(() => {
    fetchEmployees();

    // Auto-select current user if not admin
    const currentUser = getCurrentUser();
    if (currentUser.role !== 'ADMIN' && currentUser.id) {
      setSelectedEmployee(currentUser.id.toString());
    }
  }, []);

  useEffect(() => {
    fetchDispatchOrders();
  }, [selectedCourier, selectedEmployee]);

  const fetchEmployees = async () => {
    try {
      // Fetch employees for the filter dropdown
      const employeeList = await employeesApi.getAllEmployees();
      setEmployees(employeeList || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchDispatchOrders = async () => {
    try {
      setLoading(true);

      // Get current user from localStorage
      const currentUser = getCurrentUser();

      // API Call: GET /api/orders/workflow/dispatch
      const params = {};

      // Add courier filter if selected
      if (selectedCourier !== 'Couriers partner') {
        params.courier = selectedCourier;
        params.employeeId = currentUser.id;
      }

      // Add employee filter
      // If user is ADMIN, they can see all or filter by specific employee
      // If user is not ADMIN, they can only see their own orders
      if (currentUser.role === 'ADMIN') {
        if (selectedEmployee !== 'All employees') {
          console.log("selectedEmployee", selectedEmployee)
          console.log("currentUser.id", currentUser.id)
          params.employeeId = currentUser.id;
        }
      } else {
        // Non-admin users can only see their own orders
        if (currentUser.id) {
          console.log("currentUser.id", currentUser.id)
          params.employeeId = currentUser.id;
        }
      }
      console.log('Fetching dispatch orders with params:', params);
      console.log('Current user:', currentUser);

      const response = await ordersApi.getReadyToShipOrders(params);
      setOrders(response || []);
      console.log("response", response)
      setTotalOrders(response?.length || 0);
    } catch (error) {
      console.log("response", error)
      console.error('Error fetching dispatch orders:', error);
      message.error('Failed to fetch dispatch orders');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrders(orders.map(order => order.id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  // DispatchOrder.jsx - Direct implementation

  // DispatchOrder.jsx - iframe approach for better control

  const handleGenerateLabel = async () => {
    if (!selectedOrders || selectedOrders.length === 0) {
      message.warning('Please select at least one order');
      return;
    }

    try {
      message.loading({ content: 'Generating labels...', key: 'label' });

      const API_BASE_URL = API_URL;

      const response = await authFetch(`${API_BASE_URL}/workflow/labels/download/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authHelpers.getAuthToken() || ''}`
        },
        body: JSON.stringify({
          orderIds: selectedOrders
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to generate labels: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // ✅ Production-friendly approach
      // Try window.open with specific settings to avoid popup blocker
      const printWindow = window.open(
        '',
        'Print Labels',
        'width=900,height=700,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no'
      );

      if (printWindow && !printWindow.closed) {
        // Window opened successfully
        printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Print Labels - ${selectedOrders.length} Orders</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: Arial, sans-serif;
              background: #f5f5f5;
            }
            .header {
              background: #fff;
              padding: 15px 20px;
              border-bottom: 1px solid #ddd;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .btn {
              padding: 10px 20px;
              margin: 0 5px;
              border: none;
              border-radius: 5px;
              cursor: pointer;
              font-size: 14px;
              font-weight: 600;
            }
            .btn-print {
              background: #4CAF50;
              color: white;
            }
            .btn-download {
              background: #2196F3;
              color: white;
            }
            .btn-close {
              background: #f44336;
              color: white;
            }
            iframe {
              width: 100%;
              height: calc(100vh - 60px);
              border: none;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h3>Labels for ${selectedOrders.length} Orders</h3>
            <div>
              <button class="btn btn-print" onclick="window.print()">🖨️ Print</button>
              <button class="btn btn-download" onclick="downloadPDF()">⬇️ Download</button>
              <button class="btn btn-close" onclick="window.close()">✖️ Close</button>
            </div>
          </div>
          <iframe src="${blobUrl}"></iframe>
          <script>
            function downloadPDF() {
              const link = document.createElement('a');
              link.href = '${blobUrl}';
              link.download = 'labels_${Date.now()}.pdf';
              link.click();
            }
            
            // Auto-focus for print
            window.onload = () => {
              setTimeout(() => {
                // Auto-print after 1 second (optional)
                // window.print();
              }, 1000);
            };
          </script>
        </body>
        </html>
      `);
        printWindow.document.close();

        message.success({
          content: `Labels ready! (${selectedOrders.length} orders)`,
          key: 'label',
          duration: 3
        });

      } else {
        // Popup blocked - Auto download
        console.warn('Popup was blocked, downloading instead');

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `labels_${selectedOrders.length}_orders_${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        message.info({
          content: '📥 Labels downloaded! Enable popups for print preview.',
          key: 'label',
          duration: 5
        });
      }

      setSelectedOrders([]);

      // Cleanup
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 120000);

    } catch (error) {
      console.error('Error:', error);
      message.error({ content: 'Failed to generate labels', key: 'label' });
    }
  };
  // Action: View Details
  const handleViewDetails = (order) => {
    navigate(`/orders/${order.id}/details`, { state: { activeTab: 'DISPATCHED' } });
  };

  // Action: Edit Order
  const handleEditOrder = (order) => {
    navigate(`/orders/${order.id}/edit`);
  };

  // Action: Track Shipment
  const handleTrackOrder = async (order) => {
    try {
      // API Call: GET /api/orders/workflow/{orderId}/tracking
      const trackingData = await ordersApi.getOrderTracking(order.id);
      console.log('Tracking data:', trackingData);

      message.info(`Tracking Number: ${trackingData.trackingNumber}`);
    } catch (error) {
      console.error('Error tracking order:', error);
      message.error('Failed to fetch tracking information');
    }
  };

  // Action: Mark as Delivered
  const handleMarkAsDelivered = async (order) => {
    try {
      // API Call: POST /api/orders/workflow/{orderId}/mark-delivered
      const deliveryData = {
        receivedBy: order.customerName,
        deliveryNotes: 'Package delivered successfully',
        signatureUrl: ''
      };

      const response = await ordersApi.markAsDelivered(order.id, deliveryData);
      message.success(response.message || 'Order marked as delivered successfully');

      fetchDispatchOrders();
    } catch (error) {
      console.error('Error marking as delivered:', error);
      message.error('Failed to mark order as delivered');
    }
  };

  // Action: Cancel Order
  const handleCancelOrder = async (order) => {
    if (window.confirm(`Are you sure you want to cancel order ${order.orderId}?`)) {
      try {
        await ordersApi.cancelOrder(order.id);
        message.success('Order cancelled successfully');

        fetchDispatchOrders();
      } catch (error) {
        console.error('Error cancelling order:', error);
        message.error('Failed to cancel order');
      }
    }
  };

  const [courierModalOpen, setCourierModalOpen] = useState(false);
  const [courierOrder, setCourierOrder] = useState(null);
  const [courierFormData, setCourierFormData] = useState({ courierName: '', trackingNumber: '', awbNumber: '' });

  // Action: Update Courier Details
  const handleUpdateCourier = (order) => {
    setCourierOrder(order);
    setCourierFormData({
      courierName: order.courierName || '',
      trackingNumber: order.trackingNumber || '',
      awbNumber: order.awbNumber || ''
    });
    setCourierModalOpen(true);
  };

  // Action: Mark as Out for Delivery
  const handleMarkAsOFD = async (order) => {
    try {
      // API Call: POST /api/orders/workflow/{orderId}/mark-ofd
      const response = await ordersApi.markAsOFD(order.id);
      message.success(response.message || 'Order marked as out for delivery');

      fetchDispatchOrders();
    } catch (error) {
      console.error('Error marking as OFD:', error);
      message.error('Failed to mark order as out for delivery');
    }
  };

  // Action menu items
  const getActionMenuItems = (order) => [
    {
      key: 'view',
      label: (
        <div className="flex items-center space-x-2 px-2 py-1" onClick={() => handleViewDetails(order)}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          <span>View Details</span>
        </div>
      ),
    },
    {
      key: 'edit',
      label: (
        <div className="flex items-center space-x-2 px-2 py-1" onClick={() => handleEditOrder(order)}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          <span>Edit Order</span>
        </div>
      ),
    },
    {
      key: 'track',
      label: (
        <div className="flex items-center space-x-2 px-2 py-1" onClick={() => handleTrackOrder(order)}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span>Track Shipment</span>
        </div>
      ),
    },
    {
      key: 'updateCourier',
      label: (
        <div className="flex items-center space-x-2 px-2 py-1" onClick={() => handleUpdateCourier(order)}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          <span>Update Courier</span>
        </div>
      ),
    },
    {
      key: 'divider1',
      type: 'divider',
    },
    {
      key: 'ofd',
      label: (
        <div className="flex items-center space-x-2 px-2 py-1" onClick={() => handleMarkAsOFD(order)}>
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
          </svg>
          <span className="text-blue-600">Mark as OFD</span>
        </div>
      ),
    },
    {
      key: 'delivered',
      label: (
        <div className="flex items-center space-x-2 px-2 py-1" onClick={() => handleMarkAsDelivered(order)}>
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-green-600">Mark as Delivered</span>
        </div>
      ),
    },
    {
      key: 'cancel',
      label: (
        <div className="flex items-center space-x-2 px-2 py-1" onClick={() => handleCancelOrder(order)}>
          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          <span className="text-red-600">Cancel Order</span>
        </div>
      ),
    },
  ];

  const filteredOrders = orders.filter(order => {
    const matchesSearch =
      order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.orderId?.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    // Service Filtering (Client-side)
    if (selectedService !== 'All courier') {
      if (order.courierName !== selectedService) return false;
    }

    // Date Filtering
    if (selectedDateFilter !== 'All Time') {
      const orderDate = new Date(order.orderDate || order.createdAt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (isNaN(orderDate.getTime())) return false;

      const normalize = (d) => { d.setHours(0, 0, 0, 0); return d; };

      if (selectedDateFilter === 'Today') {
        if (normalize(new Date(orderDate)).getTime() !== today.getTime()) return false;
      } else if (selectedDateFilter === 'Yesterday') {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        if (normalize(new Date(orderDate)).getTime() !== yesterday.getTime()) return false;
      } else if (selectedDateFilter === 'Last Week') {
        const lastWeek = new Date(today);
        lastWeek.setDate(lastWeek.getDate() - 7);
        if (orderDate < lastWeek) return false;
      } else if (selectedDateFilter === 'Last Month') {
        const lastMonth = new Date(today);
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        if (orderDate < lastMonth) return false;
      } else if (selectedDateFilter === 'Last Six Month') {
        const lastSixMonths = new Date(today);
        lastSixMonths.setMonth(lastSixMonths.getMonth() - 6);
        if (orderDate < lastSixMonths) return false;
      }
    }

    // Modal Filters
    // 1. Agent
    if (activeFilters.agent && activeFilters.agent !== 'all') {
      if ((order.createdByName || order.agentName) !== activeFilters.agent) return false;
    }
    // 2. Category
    if (activeFilters.category && activeFilters.category !== 'all') {
      if (order.category !== activeFilters.category) return false;
    }
    // 3. Status
    if (activeFilters.status && activeFilters.status !== 'all') {
      if ((order.status || order.orderStatus) !== activeFilters.status) return false;
    }
    // 4. Payment Mode
    if (activeFilters.paymentMode && activeFilters.paymentMode !== 'all') {
      if (order.paymentMode !== activeFilters.paymentMode) return false;
    }

    return true;
  });

  const CourierUpdateModal = () => {
    if (!courierModalOpen) return null;

    const handleSubmit = async () => {
      if (courierFormData.courierName && courierFormData.trackingNumber) {
        try {
          await ordersApi.updateCourierDetails(courierOrder.id, courierFormData);
          message.success('Courier details updated successfully');
          fetchDispatchOrders();
          setCourierModalOpen(false);
        } catch (error) {
          console.error('Error updating courier:', error);
          message.error('Failed to update courier details');
        }
      } else {
        message.warning('Please fill Courier Name and Tracking Number');
      }
    };

    return (
      <div className="fixed inset-0 bg-black/60 flex items-start pt-20 justify-center z-[9999] backdrop-blur-sm transition-all duration-300">
        <div className="bg-white rounded-2xl w-full max-w-lg mx-4 shadow-2xl overflow-hidden">
          <div className="px-6 py-4 border-b flex items-center justify-between text-gray-800 bg-gray-50">
            <h3 className="text-lg font-bold flex items-center gap-2">Update Courier Details</h3>
            <button onClick={() => setCourierModalOpen(false)} className="text-gray-400 hover:text-gray-700 transition-colors p-1"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Courier Name <span className="text-red-500">*</span></label>
              <input type="text" value={courierFormData.courierName} onChange={e => setCourierFormData({ ...courierFormData, courierName: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" placeholder="Enter Courier Name" autoFocus />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Tracking Number <span className="text-red-500">*</span></label>
              <input type="text" value={courierFormData.trackingNumber} onChange={e => setCourierFormData({ ...courierFormData, trackingNumber: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" placeholder="Enter Tracking Number" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">AWB Number</label>
              <input type="text" value={courierFormData.awbNumber} onChange={e => setCourierFormData({ ...courierFormData, awbNumber: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" placeholder="Enter AWB Number" />
            </div>
          </div>
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
            <button onClick={() => setCourierModalOpen(false)} className="px-5 py-2.5 text-gray-600 font-medium hover:bg-gray-200 bg-gray-100 rounded-lg">Cancel</button>
            <button onClick={handleSubmit} className="px-6 py-2.5 text-white font-medium bg-teal-500 hover:bg-teal-600 rounded-lg flex items-center gap-2">Update</button>
          </div>
        </div>
      </div>
    );
  };

  const isDark = currentTheme.textColor === '#FFFFFF';

  return (
    <div className={`min-h-screen p-6 ${isDark ? 'bg-[#0f172a]' : 'bg-gray-50'}`}>
      <div className="flex flex-col gap-6">
      <div className="space-y-6">
      <CourierUpdateModal />
      {/* Filter Modal */}
      {isFilterModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl mx-4 overflow-hidden flex flex-col shadow-xl max-h-[90vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" style={{ background: currentTheme.color }}>
              <h2 className="text-lg font-bold text-white">Filter Orders</h2>
              <button
                onClick={() => setIsFilterModalOpen(false)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Filter Content */}
            <div className="p-6 overflow-y-auto">
              <div className="grid grid-cols-2 gap-6">

                {/* 1. Agents */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Agents</label>
                  <ThemeSelect
                    value={activeFilters.agent}
                    onChange={(val) => setActiveFilters({ ...activeFilters, agent: val })}
                    options={[
                      { label: 'All Agents', value: '' },
                      ...uniqueAgents.map(a => ({ label: a, value: a }))
                    ]}
                    icon={User}
                    className="w-full"
                  />
                </div>

                {/* 2. Category */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <ThemeSelect
                    value={activeFilters.category}
                    onChange={(val) => setActiveFilters({ ...activeFilters, category: val })}
                    options={[
                      { label: 'All Categories', value: '' },
                      ...uniqueCategories.map(c => ({ label: c, value: c }))
                    ]}
                    icon={LayoutGrid}
                    className="w-full"
                  />
                </div>

                {/* 3. Status */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <ThemeSelect
                    value={activeFilters.status}
                    onChange={(val) => setActiveFilters({ ...activeFilters, status: val })}
                    options={[
                      { label: 'All Status', value: '' },
                      ...uniqueStatuses.map(s => ({ label: s, value: s }))
                    ]}
                    icon={CheckCircle}
                    className="w-full"
                  />
                </div>

                {/* 4. Payment Mode */}
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode</label>
                  <ThemeSelect
                    value={activeFilters.paymentMode}
                    onChange={(val) => setActiveFilters({ ...activeFilters, paymentMode: val })}
                    options={[
                      { label: 'All Modes', value: '' },
                      { label: 'COD', value: 'COD' },
                      { label: 'PREPAID', value: 'PREPAID' }
                    ]}
                    icon={CreditCard}
                    className="w-full"
                  />
                </div>

              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end px-6 py-4 border-t border-gray-200 space-x-3 bg-gray-50">
              <button
                onClick={() => setActiveFilters({
                  agent: '', category: '', status: '', paymentMode: ''
                })}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Reset
              </button>
              <button
                onClick={() => setIsFilterModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                style={{ backgroundColor: currentTheme.color }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl desktop:text-3xl font-bold text-gray-800">
          Dispatch Order <span className="text-gray-500 text-xl desktop:text-2xl">({totalOrders})</span>
        </h1>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsFilterModalOpen(true)}
            className="p-2 desktop:p-3 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Filter className="w-5 h-5 desktop:w-6 desktop:h-6 text-gray-600" />
          </button>
          <button
            onClick={() => setShowBulkModal(true)}
            className="flex items-center space-x-2 px-4 py-2 desktop:px-6 desktop:py-3 border border-gray-300 rounded-lg hover:bg-gray-50 desktop:text-lg"
          >
            <CheckCircle className="w-5 h-5 desktop:w-6 desktop:h-6 text-gray-600" />
            <span>Bulk status update</span>
          </button>
          <button className="p-3 desktop:p-4 text-white rounded-lg transition-colors" style={{ backgroundColor: currentTheme.color }}>
            <Plus className="w-5 h-5 desktop:w-6 desktop:h-6" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <ExpandableSearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          placeholder="Search by lead id or customer name"
        />

        <select
          value={`${orderLimit} Orders`}
          onChange={(e) => {
            setOrderLimit(parseInt(e.target.value));
            setCurrentPage(1);
          }}
          className="px-4 py-2.5 desktop:px-6 desktop:py-4 border border-gray-300 rounded-lg focus:outline-none desktop:text-lg transition-all"
          style={{ borderColor: orderLimit ? currentTheme.color : '#D1D5DB' }}
        >
          <option value="20 Orders">20 Orders</option>
          <option value="50 Orders">50 Orders</option>
          <option value="100 Orders">100 Orders</option>
        </select>

        {/* Employee Filter */}
        <select
          value={selectedEmployee}
          onChange={(e) => setSelectedEmployee(e.target.value)}
          disabled={getCurrentUser().role !== 'ADMIN'}
          className={`px-4 py-2.5 desktop:px-6 desktop:py-4 border border-gray-300 rounded-lg focus:outline-none desktop:text-lg transition-all ${getCurrentUser().role !== 'ADMIN'
            ? 'bg-gray-100 cursor-not-allowed'
            : ''
            }`}
          style={{ borderColor: selectedEmployee !== 'All employees' ? currentTheme.color : '#D1D5DB' }}
        >
          <option value="All employees">All employees</option>
          {employees.map((employee) => (
            <option key={employee.id} value={employee.id}>
              {employee.name || `${employee.firstName || ''} ${employee.lastName || ''}`.trim()}
            </option>
          ))}
        </select>

        {/* Courier Filter */}
        <div className="relative">
          <select
            value={selectedCourier}
            onChange={(e) => setSelectedCourier(e.target.value)}
            className="appearance-none pl-10 pr-8 py-2.5 desktop:py-4 border border-gray-300 rounded-full focus:outline-none desktop:text-lg cursor-pointer bg-white text-gray-600 hover:border-gray-400 transition-colors"
          >
            {courierOptions.map(courier => (
              <option key={courier} value={courier}>{courier}</option>
            ))}
          </select>
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Truck className="h-5 w-5" style={{ color: currentTheme.color }} />
          </div>
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Courier Service Filter (Client-side) */}
        <div className="relative">
          <select
            value={selectedService}
            onChange={(e) => setSelectedService(e.target.value)}
            className="appearance-none pl-10 pr-8 py-2.5 desktop:py-4 rounded-full focus:outline-none desktop:text-lg cursor-pointer text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: currentTheme.color }}
          >
            <option value="All courier">All courier</option>
            {uniqueServices.map(service => (
              <option key={service} value={service} className="text-gray-900">{service}</option>
            ))}
          </select>
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Truck className="h-5 w-5 text-white" />
          </div>
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Date Filter */}
        <div className="relative">
          <select
            value={selectedDateFilter}
            onChange={(e) => setSelectedDateFilter(e.target.value)}
            className="appearance-none pl-10 pr-8 py-2.5 desktop:py-4 border border-gray-300 rounded-full focus:outline-none desktop:text-lg cursor-pointer bg-white text-gray-600 hover:border-gray-400 transition-colors"
          >
            {dateOptions.map(date => (
              <option key={date} value={date}>{date}</option>
            ))}
          </select>
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Calendar className="h-5 w-5 text-orange-500" />
          </div>
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="text-white" style={{ background: currentTheme.color }}>
              <tr>
                <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left">
                  <Checkbox
                    checked={selectedOrders.length === orders.length && orders.length > 0}
                    onChange={handleSelectAll}
                  />
                  <span className="ml-2 text-xs desktop:text-sm">ALL</span>
                </th>
                <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-sm desktop:text-base font-medium">ORDER ID</th>
                <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-sm desktop:text-base font-medium">DATE</th>
                <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-sm desktop:text-base font-medium">NAME</th>
                <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-sm desktop:text-base font-medium">PRODUCT</th>
                <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-sm desktop:text-base font-medium">CATEGORY</th>
                <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-sm desktop:text-base font-medium">REST AMT.</th>
                <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-sm desktop:text-base font-medium">PAY MODE</th>
                <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-sm desktop:text-base font-medium">ORDER CAMPAIGN</th>
                <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-sm desktop:text-base font-medium">ORDER BY</th>
                <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-left text-sm desktop:text-base font-medium">ACTION BY</th>
                <th className="px-4 py-3 desktop:px-6 desktop:py-4 text-center text-sm desktop:text-base font-medium">ACTION</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="12" className="px-4 py-8 text-center">
                    <div className="inline-block w-6 h-6 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: currentTheme.color, borderTopColor: 'transparent' }}></div>
                  </td>
                </tr>
              ) : filteredOrders.length > 0 ? (
                filteredOrders
                  .slice((currentPage - 1) * orderLimit, currentPage * orderLimit)
                  .map((order, index) => (
                    <tr
                      key={order.id}
                      className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    >
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedOrders.includes(order.id)}
                          onChange={() => handleSelectOrder(order.id)}
                        />
                      </td>
                      <td
                        className="px-4 py-3 desktop:px-6 desktop:py-5 text-sm desktop:text-base font-medium text-gray-900 cursor-pointer transition-colors"
                        style={{ color: 'inherit' }}
                        onMouseEnter={(e) => e.target.style.color = currentTheme.color}
                        onMouseLeave={(e) => e.target.style.color = 'inherit'}
                        onClick={() => handleViewDetails(order)}
                      >
                        {order.orderId}
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-sm desktop:text-base text-gray-700">
                        {order.orderDate ? new Date(order.orderDate).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className="flex flex-col">
                          <span className="text-sm desktop:text-base font-medium text-gray-900">{order.customerName}</span>
                          <div className="flex items-center space-x-2 mt-1">
                            <svg className="w-3 h-3 desktop:w-4 desktop:h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span className="text-xs desktop:text-sm text-gray-600">{order.phone || order.contactNumber}</span>
                            {order.hasWhatsapp && (
                              <div className="w-4 h-4 desktop:w-5 desktop:h-5 bg-green-500 rounded-full flex items-center justify-center">
                                <svg className="w-3 h-3 desktop:w-4 desktop:h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-sm desktop:text-base text-gray-700">
                        {Array.isArray(order.productName) ? order.productName.join(', ') : order.productName || '-'}
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-sm desktop:text-base text-gray-700">{order.category || '-'}</td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-sm desktop:text-base font-bold" style={{ color: currentTheme.color }}>₹ {order.amount || order.restAmount || 0}</td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-sm desktop:text-base text-gray-700">{order.paymentMode || '-'}</td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5 text-sm desktop:text-base text-gray-700">{order.leadSource || '-'}</td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className="flex flex-col">
                          <span className="text-sm desktop:text-base font-medium text-gray-900">{order.orderBy || '-'}</span>
                          <span className="text-xs desktop:text-sm text-gray-600">Emp: {order.orderByEmp || '-'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5">
                        <div className="flex flex-col">
                          <span className="text-sm desktop:text-base font-medium text-gray-900">{order.actionBy || '-'}</span>
                          <span className="text-xs desktop:text-sm text-gray-600">Emp: {order.actionByEmp || ''}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 desktop:px-6 desktop:py-5" onClick={(e) => e.stopPropagation()}>
                        <Dropdown
                          menu={{ items: getActionMenuItems(order) }}
                          trigger={['click']}
                          placement="bottomRight"
                        >
                          <button className="p-1 hover:bg-gray-200 rounded-lg transition-colors">
                            <MoreVertical className="w-5 h-5 desktop:w-6 desktop:h-6 text-gray-600" />
                          </button>
                        </Dropdown>
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan="12" className="px-4 py-8 text-center text-gray-500">
                    No orders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-4">
        <Pagination
          currentPage={currentPage}
          totalItems={filteredOrders.length}
          itemsPerPage={orderLimit}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>

      {/* Generate Label Button */}
      {selectedOrders.length > 0 && (
        <div className="fixed bottom-8 right-8">
          <button
            onClick={handleGenerateLabel}
            className="flex items-center space-x-2 px-6 py-3 desktop:px-8 desktop:py-4 text-white rounded-lg shadow-lg transition-all desktop:text-lg"
            style={{ backgroundColor: currentTheme.color }}
          >
            <svg className="w-5 h-5 desktop:w-6 desktop:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            <span>Generate label ({selectedOrders.length})</span>
          </button>
        </div>
      )}

      {/* Bulk Status Update Modal */}
      <BulkStatusUpdateModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onSave={(file) => {
          console.log('File uploaded:', file);
          setShowBulkModal(false);
          fetchDispatchOrders();
        }}
      />
    </div>
      </div>
    </div>
  );
}

export default DispatchOrder;